import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useReportTagActions } from "@features/report/tag/hooks/useReportTagActions";
import { useReportTagStatus } from "@features/report/tag/hooks/useReportTags";
import InfoTooltip from "@core/components/InfoTooltip";
import { getReportTagStatusLabel } from "@core/reports/utils/reportWorkflow";

const RequestReopenModal: React.FC<{
    show: boolean;
    onHide: () => void;
    tag: { report_tag_id: number; name: string } | null;
    nationId: number;
    onRequested?: () => void;
}> = ({ show, onHide, tag, nationId, onRequested }) => {
    const [selectedStatusId, setSelectedStatusId] = useState<number>(0);
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doRequestReopen } = useReportTagActions();

    const { data: statuses, loading: statusesLoading } = useReportTagStatus({
        all: true,
    });

    // Filter out CLOSED status — user picks PENDING or OPEN
    const reopenStatuses = statuses.filter(
        (s: any) => s.status_name?.toUpperCase() !== "CLOSED"
    );

    const handleClose = () => {
        onHide();
        setSelectedStatusId(0);
    };

    const handleSubmit = async () => {
        const messages: string[] = [];
        if (!tag) messages.push('No tag selected.');
        if (!selectedStatusId) messages.push('Please select a status.');
        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        try {
            const response = await doRequestReopen(nationId, tag!.report_tag_id, {
                next_report_tag_status_id: selectedStatusId,
            });

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "Reopen request submitted.");
                handleClose();
                onRequested?.();
            } else {
                showError(response.getError() as ErrorResponse);
            }
        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    Request Reopen
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <p>
                    Request to reopen tag <strong>{tag?.name}</strong>.
                </p>
                <Form.Group controlId="reopenStatus" className="mb-0">
                    <Form.Label>Desired Status<span className="text-danger">*</span><InfoTooltip text="Choose the status you want this tag to have after reopening (e.g. Open so submissions can continue)." /></Form.Label>
                    <Form.Select
                        aria-label="Desired status"
                        value={selectedStatusId}
                        onChange={(e) => setSelectedStatusId(Number(e.target.value))}
                        disabled={statusesLoading}
                    >
                        <option value={0}>Select status</option>
                        {reopenStatuses.map((s: any) => (
                            <option key={s.report_tag_status_id} value={s.report_tag_status_id}>
                                {getReportTagStatusLabel(s.status_name)}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="outline-secondary" onClick={handleClose} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    className="btn-action"
                    onClick={handleSubmit}
                    disabled={busy}
                    aria-busy={busy}
                >
                    {busy ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Submitting...
                        </>
                    ) : (
                        "Submit Request"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RequestReopenModal;
